import {
  Component,
  OnInit,
  OnDestroy,
  Renderer2,
  ViewChild,
  ElementRef,
  Directive,
} from '@angular/core';

import {
  Router,
  NavigationEnd,
} from '@angular/router';
import { Subject } from 'rxjs';
import {
  Location,
} from '@angular/common';
import { first, filter, takeUntil } from 'rxjs/operators';
import { PortalService } from '@app/_services/Portal/portal.services';
import { AccountService } from '@app/_services';
import { UsuariosServicesService } from '@app/_services/Usuarios/usuarios-services.service';
import swal from 'sweetalert2';
import { ResponseService, UserData } from '@app/_models';
import jwt_decode from 'jwt-decode';

// Constantes para mejor mantenibilidad
const MISC_DEFAULTS = {
  navbar_menu_visible: 0,
  active_collapse: true,
  disabled_collapse_init: 0,
  sidebar_mini_active: false,
  hide_sidebar_active: false,
};

const PASSWORD_REGEX = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

const ROLES = {
  ASOCIADO_PRE: 'AsociadoPre',
  ASOCIADO: 'Asociado'
} as const;

interface TokenInfo {
  RFC: string;
  Role: string;
  [key: string]: any;
}

interface ChangePasswordData {
  usuario: string;
  contrasena: string;
  nuevaContrasena: string;
}

declare var $: any;
@Component({
    selector: 'app-navbar-cmp',
    templateUrl: 'navbar.component.html',
    styleUrls: ['./navbar.component.less'],
    standalone: false
})
export class NavbarComponent implements OnInit, OnDestroy {
  // Subject para manejo de memoria
  private readonly destroy$ = new Subject<void>();
  
  // ViewChild
  @ViewChild('app-navbar-cmp', { static: false }) button: any;

  // State management
  private listTitles: any[] = [];
  private misc = { ...MISC_DEFAULTS };
  private nativeElement: Node;
  private toggleButton: any;
  private sidebarVisible = false;
  
  public dataUser: UserData;
  public isAsociadoPre = false;
  public isAsociado = false;
  public usuario: string;
  public mobile_menu_visible = 0;

  constructor(
    public location: Location,
    private readonly renderer: Renderer2,
    private readonly element: ElementRef,
    private readonly router: Router,
    private readonly portalService: PortalService,
    private readonly accountService: AccountService,
    private readonly _usuarioService: UsuariosServicesService
  ) {
    this.nativeElement = element.nativeElement;
  }

  ngOnInit(): void {
    this.initializeUserData();
    this.initializeNavbar();
    this.setupRouterSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa los datos del usuario y el menú
   */
  private initializeUserData(): void {
    this.dataUser = this.accountService.userInfo;

    if (!this.dataUser) return;

    const user = this.accountService.userValue;
    if (!user?.token) return;

    const tokenInfo = this.getDecodedAccessToken(user.token);
    
    if (tokenInfo) {
      this.setUserRole(tokenInfo.Role);
      this.loadMenu();
    }
  }

  /**
   * Establece el rol del usuario
   */
  private setUserRole(role: string): void {
    this.isAsociadoPre = role === ROLES.ASOCIADO_PRE;
    this.isAsociado = role === ROLES.ASOCIADO;
  }

  /**
   * Carga el menú basado en el rol
   */
  private loadMenu(): void {
    this.portalService
      .getMenuByRole(this.dataUser.Role)
      .pipe(
        first(),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          this.listTitles = result || [];
        },
        error: (error) => {
          console.error('Error loading menu:', error);
          this.listTitles = [];
        },
      });
  }

  /**
   * Inicializa la configuración del navbar
   */
  private initializeNavbar(): void {
    const navbar: HTMLElement = this.element.nativeElement;
    const body = document.getElementsByTagName('body')[0];
    
    this.toggleButton = navbar.getElementsByClassName('navbar-toggler')[0];
    
    if (body.classList.contains('sidebar-mini')) {
      this.misc.sidebar_mini_active = true;
    }
    if (body.classList.contains('hide-sidebar')) {
      this.misc.hide_sidebar_active = true;
    }
  }

  /**
   * Configura la suscripción al router
   */
  private setupRouterSubscription(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.sidebarClose();
        this.removeCloseLayer();
      });
  }

  /**
   * Remueve la capa de cierre
   */
  private removeCloseLayer(): void {
    const layer = document.getElementsByClassName('close-layer')[0];
    if (layer) {
      layer.remove();
    }
  }

  /**
   * Decodifica el token JWT
   */
  private getDecodedAccessToken(token: string): TokenInfo | null {
    try {
      return jwt_decode(token);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
  // ========== Sidebar Methods ==========

  /**
   * Minimiza el sidebar
   */
  minimizeSidebar(): void {
    const body = document.getElementsByTagName('body')[0];

    if (this.misc.sidebar_mini_active) {
      body.classList.remove('sidebar-mini');
      this.misc.sidebar_mini_active = false;
    } else {
      setTimeout(() => {
        body.classList.add('sidebar-mini');
        this.misc.sidebar_mini_active = true;
      }, 300);
    }

    this.simulateWindowResize();
  }

  /**
   * Oculta el sidebar
   */
  hideSidebar(): void {
    const body = document.getElementsByTagName('body')[0];
    const sidebar = document.getElementsByClassName('sidebar')[0];

    if (this.misc.hide_sidebar_active) {
      setTimeout(() => {
        body.classList.remove('hide-sidebar');
        this.misc.hide_sidebar_active = false;
      }, 300);
      setTimeout(() => {
        sidebar?.classList.remove('animation');
      }, 600);
      sidebar?.classList.add('animation');
    } else {
      setTimeout(() => {
        body.classList.add('hide-sidebar');
        this.misc.hide_sidebar_active = true;
      }, 300);
    }

    this.simulateWindowResize();
  }

  /**
   * Simula el resize de la ventana para actualizar gráficos
   */
  private simulateWindowResize(): void {
    const simulateWindowResize = setInterval(() => {
      window.dispatchEvent(new Event('resize'));
    }, 180);

    setTimeout(() => {
      clearInterval(simulateWindowResize);
    }, 1000);
  }

  /**
   * Abre el sidebar en móvil
   */
  sidebarOpen(): void {
    const toggle = document.getElementsByClassName('navbar-toggler')[0];
    const body = document.getElementsByTagName('body')[0];
    
    setTimeout(() => {
      this.toggleButton?.classList.add('toggled');
    }, 500);
    
    body.classList.add('nav-open');
    
    setTimeout(() => {
      toggle?.classList.add('toggled');
    }, 430);

    const layer = document.createElement('div');
    layer.setAttribute('class', 'close-layer');

    if (body.querySelectorAll('.main-panel').length) {
      document.getElementsByClassName('main-panel')[0].appendChild(layer);
    } else if (body.classList.contains('off-canvas-sidebar')) {
      document.getElementsByClassName('wrapper-full-page')[0].appendChild(layer);
    }

    setTimeout(() => {
      layer.classList.add('visible');
    }, 100);

    layer.onclick = () => {
      body.classList.remove('nav-open');
      this.mobile_menu_visible = 0;
      this.sidebarVisible = false;

      layer.classList.remove('visible');
      setTimeout(() => {
        layer.remove();
        toggle?.classList.remove('toggled');
      }, 400);
    };

    this.mobile_menu_visible = 1;
    this.sidebarVisible = true;
  }

  /**
   * Cierra el sidebar
   */
  sidebarClose(): void {
    const toggle = document.getElementsByClassName('navbar-toggler')[0];
    const body = document.getElementsByTagName('body')[0];
    
    this.toggleButton?.classList.remove('toggled');

    this.sidebarVisible = false;
    body.classList.remove('nav-open');
    
    this.removeCloseLayer();

    setTimeout(() => {
      toggle?.classList.remove('toggled');
    }, 400);

    this.mobile_menu_visible = 0;
  }

  /**
   * Toggle del sidebar
   */
  sidebarToggle(): void {
    if (this.sidebarVisible) {
      this.sidebarClose();
    } else {
      this.sidebarOpen();
    }
  }

  /**
   * Maneja el resize de la ventana
   */
  onResize(event: any): boolean {
    return $(window).width() <= 991;
  }

  /**
   * Obtiene el título de la ruta actual
   */
  getTitle(): string {
    let title = this.location.prepareExternalUrl(this.location.path());
    
    if (title.charAt(0) === '#') {
      title = title.slice(1);
    }

    for (const element of this.listTitles) {
      if (element.type === 'link' && element.path === title) {
        return element.title;
      } else if (element.type === 'sub') {
        for (const child of element.children || []) {
          const subtitle = `${element.path}/${child.path}`;
          if (subtitle === title) {
            return child.title;
          }
        }
      }
    }
    
    return 'Dashboard';
  }

  /**
   * Obtiene la ruta actual
   */
  getPath(): string {
    return this.location.prepareExternalUrl(this.location.path());
  }

  /**
   * Verifica si hay un usuario autenticado
   */
  isUser(): boolean {
    return this.dataUser !== null;
  }
  // ========== Password Change Methods ==========

  /**
   * Muestra el diálogo para cambiar contraseña
   */
  ValidChangePassword(): void {
    swal.fire({
      title: 'Cambio de Contraseña',
      html: this.getPasswordChangeHTML(),
      showCancelButton: true,
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-danger',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        this.processPasswordChange();
      }
    });
  }

  /**
   * Obtiene el HTML del formulario de cambio de contraseña
   */
  private getPasswordChangeHTML(): string {
    return `
      <br />
      <form method="post" id="taxcode-update" name="taxcodeUpdate">
        <input id="input-contrasena" autofocus class="form-control wedding-input-text wizard-input-pad" 
               type="password" name="taxCode" placeholder="Contraseña Actual">
        <input id="input-contrasenaNueva" class="form-control wedding-input-text wizard-input-pad" 
               type="password" placeholder="Contraseña Nueva">
      </form>
    `;
  }

  /**
   * Procesa el cambio de contraseña
   */
  private processPasswordChange(): void {
    const currentPassword = $('#input-contrasena').val();
    const newPassword = $('#input-contrasenaNueva').val();

    if (!currentPassword || !newPassword) {
      this.showError(
        'Es obligatorio ingresar los dos campos',
        'warning'
      );
      return;
    }

    if (!this.testInputPasswordRegExp(newPassword)) {
      this.showPasswordValidationError();
      return;
    }

    this.cambiarContrasena(currentPassword, newPassword);
  }

  /**
   * Valida el formato de la contraseña
   */
  private testInputPasswordRegExp(text: string): boolean {
    return PASSWORD_REGEX.test(text);
  }

  /**
   * Muestra el error de validación de contraseña
   */
  private showPasswordValidationError(): void {
    swal.fire({
      icon: 'warning',
      html: `
        <div>
          <h4>Error de validación, la contraseña debe contener:</h4>
          <ol>
            <li style="text-align: left;">Al menos una letra mayúscula</li>
            <li style="text-align: left;">Al menos una letra minúscula</li>
            <li style="text-align: left;">Al menos un dígito</li>
            <li style="text-align: left;">Al menos un caracter especial</li>
            <li style="text-align: left;">Mínimo ocho de longitud</li>
          </ol>
        </div>
      `,
      customClass: {
        confirmButton: 'btn btn-success',
      },
      buttonsStyling: false,
    });
  }

  /**
   * Cambia la contraseña del usuario
   */
  private cambiarContrasena(oldPassword: string, newPassword: string): void {
    this.showLoading('Procesando información');

    const payload: ChangePasswordData = {
      usuario: this.dataUser.RFC,
      contrasena: oldPassword,
      nuevaContrasena: newPassword,
    };

    this._usuarioService.CambioPassword(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ResponseService) => {
          this.handlePasswordChangeResponse(data);
        },
        error: (error) => {
          console.error('Error changing password:', error);
          this.showError('Ocurrió un error, vuelva a intentarlo nuevamente!');
        }
      });
  }

  /**
   * Maneja la respuesta del cambio de contraseña
   */
  private handlePasswordChangeResponse(data: ResponseService): void {
    if (!data.hasError) {
      swal.fire({
        title: 'Contraseña Actualizada Correctamente',
        text: 'El cambio se verá reflejado cuando ingreses nuevamente',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-success',
        },
        icon: 'success',
      });
    } else {
      this.showError(data.ErrorMsg);
    }
  }

  // ========== Métodos Auxiliares ==========

  private showLoading(message: string): void {
    swal.fire(message);
    swal.showLoading();
  }

  private showError(text: string, icon: any = 'error', title: string = '¡Error!'): void {
    swal.close();
    swal.fire({
      icon,
      title,
      text
    });
  }
}
